import { useEffect, useState, useCallback } from "react";
import { Loader2, X, Search, Building2, ShieldCheck, Star, Ban, RotateCcw, Check } from "lucide-react";
import { toast } from "sonner";
import { api, mediaSrc } from "@/lib/api";
import { money, StatusBadge, OVERLINE, H1, Panel, btnSecondary, useAdmin } from "@/components/admin/ui";

const ActionBtn = ({ onClick, icon: Icon, children, testid, danger }) => (
  <button onClick={onClick} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border transition-colors ${danger ? "border-[#D9381E]/40 text-[#D9381E] hover:bg-[#D9381E] hover:text-white" : "border-[#1F2A22]/30 text-[#1F2A22] hover:bg-[#1F2A22] hover:text-white"}`} data-testid={testid}>
    <Icon size={13} /> {children}
  </button>
);

export function BusinessDrawer({ id, onClose, onChanged, canManage = true }) {
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => { api.adminBusinessDetail(id).then(setD).catch(() => setD(false)); }, [id]);
  useEffect(() => { load(); }, [load]);
  const act = async (action, extra = {}) => {
    try { await api.adminBusinessAction(id, { action, ...extra }); toast.success("Updated"); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addNote = async () => { if (!note.trim()) return; try { await api.adminBusinessNote(id, note.trim()); setNote(""); toast.success("Note added"); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="fixed inset-0 z-50" data-testid="business-drawer">
      <div className="absolute inset-0 bg-[#1F2A22]/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-lg bg-[#F9F8F6] border-l border-[#E5E2DC] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E2DC] bg-white sticky top-0">
          <h3 className="font-serif text-xl text-[#1F2A22]">Business</h3>
          <button onClick={onClose} aria-label="Close" data-testid="biz-drawer-close"><X size={20} className="text-[#5C6B61]" /></button>
        </div>
        {d === null ? <div className="p-10 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>
          : d === false ? <div className="p-6 text-sm text-[#5C6B61]">Not found.</div> : (
          <div className="p-6 space-y-5">
            <div className="flex items-center gap-3">
              {d.logo ? <img src={mediaSrc(d.logo)} alt="" className="w-14 h-14 object-cover border border-[#E5E2DC]" /> : <div className="w-14 h-14 bg-[#F2F0EB] grid place-items-center"><Building2 className="text-[#8A5A44]" /></div>}
              <div>
                <p className="font-serif text-xl text-[#1F2A22]">{d.name}{d.featured && <Star size={14} className="inline ml-2 text-[#FF6A00] fill-[#FF6A00]" />}</p>
                <p className="text-xs text-[#5C6B61]">{d.category} · {d.location || "—"}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <StatusBadge status={d.approval_status} /><StatusBadge status={d.account_status} /><StatusBadge status={d.subscription_status} />
              <span className="text-[11px] uppercase bg-[#8A5A44]/15 text-[#8A5A44] px-2.5 py-0.5 rounded-full">{d.verification_status}</span>
            </div>
            {d.stats && (
              <div className="grid grid-cols-4 gap-2 text-center">
                {[["Listings", d.stats.listings], ["Orders", d.stats.orders], ["GMV", money(d.stats.gmv_cents)], ["HR Rev", money(d.stats.hr_revenue_cents)]].map(([k, v]) => (
                  <div key={k} className="border border-[#E5E2DC] bg-white p-2"><p className="font-serif text-lg text-[#1F2A22]">{v}</p><p className="text-[10px] uppercase text-[#5C6B61]">{k}</p></div>
                ))}
              </div>
            )}
            <div className="border border-[#E5E2DC] bg-white divide-y divide-[#E5E2DC] text-sm">
              {[["Contact", `${d.contact_name || "—"}`], ["Email", d.contact_email || "—"], ["Website", d.website || "—"], ["Plan", d.plan || "—"], ["Created", (d.created_at || "").slice(0, 10)]].map(([k, v]) => (
                <div key={k} className="flex justify-between px-4 py-2"><span className="text-[#5C6B61]">{k}</span><span className="text-[#1F2A22] text-right truncate max-w-[60%]">{v}</span></div>
              ))}
            </div>
            {canManage && (
              <div className="flex flex-wrap gap-2">
                <ActionBtn onClick={() => act("verify")} icon={ShieldCheck} testid="biz-verify">Verify</ActionBtn>
                <ActionBtn onClick={() => act("unverify")} icon={X} testid="biz-unverify">Unverify</ActionBtn>
                {d.featured ? <ActionBtn onClick={() => act("unfeature")} icon={Star} testid="biz-unfeature">Unfeature</ActionBtn> : <ActionBtn onClick={() => act("feature")} icon={Star} testid="biz-feature">Feature</ActionBtn>}
                {d.account_status === "suspended" ? <ActionBtn onClick={() => act("reactivate")} icon={RotateCcw} testid="biz-reactivate">Reactivate</ActionBtn> : <ActionBtn onClick={() => act("suspend")} icon={Ban} testid="biz-suspend" danger>Suspend</ActionBtn>}
                <ActionBtn onClick={() => act("approve")} icon={Check} testid="biz-approve">Approve</ActionBtn>
                <ActionBtn onClick={() => act("reject")} icon={X} testid="biz-reject" danger>Reject</ActionBtn>
              </div>
            )}
            <div>
              <span className={OVERLINE}>Internal notes</span>
              <div className="space-y-2 mt-2">
                {(d.notes || []).map((n) => (
                  <div key={n.id} className="border border-[#E5E2DC] bg-white px-3 py-2 text-sm"><p className="text-[#1F2A22]">{n.text}</p><p className="text-[10px] text-[#5C6B61] mt-1">{n.by} · {(n.at || "").slice(0, 16).replace("T", " ")}</p></div>
                ))}
                {(!d.notes || d.notes.length === 0) && <p className="text-xs text-[#5C6B61]">No notes yet.</p>}
              </div>
              {canManage && (
                <div className="flex gap-2 mt-2">
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" className="flex-1 border border-[#E5E2DC] bg-white px-3 py-2 text-sm outline-none focus:border-[#1F2A22]" data-testid="biz-note-input" />
                  <button onClick={addNote} className={btnSecondary} data-testid="biz-note-add">Add</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function BusinessTable({ rows, onOpen }) {
  return (
    <div className="mt-4 bg-white border border-[#E5E2DC] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#F2F0EB] text-[#5C6B61] uppercase tracking-wide text-[11px]">
          <tr>{["Business", "Category", "Plan", "Subscription", "Verification", "Listings", "GMV", "HR Rev"].map((h) => <th key={h} className="text-left font-serif p-3 whitespace-nowrap">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.id} onClick={() => onOpen(b.id)} className="border-t border-[#E5E2DC] hover:bg-[#F9F8F6] cursor-pointer" data-testid="business-row">
              <td className="p-3 whitespace-nowrap font-medium text-[#1F2A22]">{b.name}{b.featured && <Star size={12} className="inline ml-1.5 text-[#FF6A00] fill-[#FF6A00]" />}</td>
              <td className="p-3 text-[#5C6B61] capitalize">{b.category || "—"}</td>
              <td className="p-3 capitalize">{b.plan || "—"}</td>
              <td className="p-3"><StatusBadge status={b.subscription_status} /></td>
              <td className="p-3 text-[#5C6B61] text-xs">{b.verification_status}</td>
              <td className="p-3 tabular-nums">{b.stats?.listings ?? 0}</td>
              <td className="p-3 tabular-nums">{money(b.stats?.gmv_cents)}</td>
              <td className="p-3 tabular-nums text-[#8A5A44]">{money(b.stats?.hr_revenue_cents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminBusinesses() {
  const { admin } = useAdmin();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("");
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => { setRows(null); api.adminBusinesses({ q: q || undefined, plan: plan || undefined }).then((d) => setRows(d.businesses)).catch(() => setRows([])); }, [q, plan]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 md:p-8" data-testid="admin-businesses">
      <span className={OVERLINE}>Network</span>
      <h1 className={`${H1} mt-1`}>Businesses</h1>
      <div className="flex flex-wrap gap-2 mt-6">
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C6B61]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search businesses…" className="border border-[#E5E2DC] bg-white pl-8 pr-3 py-2 text-sm outline-none focus:border-[#1F2A22]" data-testid="biz-search" />
        </div>
        <select value={plan} onChange={(e) => setPlan(e.target.value)} className="border border-[#E5E2DC] bg-white px-3 py-2 text-sm" data-testid="biz-filter-plan">
          <option value="">All plans</option><option value="business">Business</option><option value="pro">Pro</option><option value="elite">Elite</option>
        </select>
      </div>
      {rows === null ? <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>
        : rows.length === 0 ? <div className="mt-8 border border-dashed border-[#E5E2DC] bg-white p-16 text-center text-[#5C6B61]"><Building2 size={28} className="mx-auto mb-2 text-[#E5E2DC]" />No businesses found.</div>
        : <BusinessTable rows={rows} onOpen={setOpenId} />}
      <p className="text-sm text-[#5C6B61] mt-3">{rows?.length || 0} businesses</p>
      {openId && <BusinessDrawer id={openId} onClose={() => setOpenId(null)} onChanged={load} canManage={admin?.permissions?.includes("businesses")} />}
    </div>
  );
}
